import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { auth, googleProvider } from "../firebase-config";
import { signInWithPopup } from "firebase/auth";
import bynLogo from "./byn-logo.jpeg";

const API_BASE = process.env.REACT_APP_API_BASE;

const Login = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");

  // ✅ When pressing BACK on login → go straight to Welcome
  useEffect(() => {
    const handleBack = () => {
      navigate("/welcome", { replace: true });
    };
    window.addEventListener("popstate", handleBack);
    return () => window.removeEventListener("popstate", handleBack);
  }, [navigate]);

  // ✅ Handle Email + Password Login (MongoDB)
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!email.trim() || !password.trim()) {
      setMessage("Please enter your email and password.");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/api/customers/login`, {
        email,
        password,
      });

      const { token, _id } = response.data;
      localStorage.setItem("userEmail", email);
      localStorage.setItem("userToken", token);
      localStorage.setItem("customerId", _id);

      const profileRes = await axios.get(
        `${API_BASE}/api/customers/user-profile/${email}`
      );
      const profile = profileRes.data;

      if (!profile.name || !profile.lastName)
        return navigate("/complete-profile", { replace: true });
      if (!profile.phone)
        return navigate("/verify-phone", { replace: true });
      if (!profile.address)
        return navigate("/set-address", { replace: true });
      if (!profile.profilePicture)
        return navigate("/upload-photo", { replace: true });

      navigate("/terms", { replace: true });
    } catch (error) {
      console.error("❌ Login error:", error);
      setMessage(
        error.response?.data?.message ||
          "Login failed. Please try again."
      );
    }
  };

  // ✅ Handle Google Sign-In
  const handleGoogleLogin = async () => {
    setMessage("");
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const loginRes = await axios.post(
        `${API_BASE}/api/customers/google-login`,
        {
          name: user.displayName,
          email: user.email,
          googleId: user.uid,
        }
      );

      const { token, _id } = loginRes.data;
      localStorage.setItem("userEmail", user.email);
      localStorage.setItem("userToken", token);
      localStorage.setItem("customerId", _id);

      const profileRes = await axios.get(
        `${API_BASE}/api/customers/user-profile/${user.email}`
      );
      const profile = profileRes.data;

      if (!profile.name || !profile.lastName)
        return navigate("/complete-profile", { replace: true });
      if (!profile.phone)
        return navigate("/verify-phone", { replace: true });
      if (!profile.address)
        return navigate("/set-address", { replace: true });
      if (!profile.profilePicture)
        return navigate("/upload-photo", { replace: true });

      navigate("/terms", { replace: true });
    } catch (error) {
      console.error("🚨 Google login error:", error);
      setMessage("Google Sign-In failed.");
    }
  };

  return (
    <div className="min-h-screen bg-white px-6 py-12">
      <div className="max-w-md mx-auto space-y-6">

        {/* 🔥 BIG CENTERED LOGO */}
        <div className="flex justify-center mb-6">
          <img
            src={bynLogo}
            alt="Book Your Need"
            className="w-48 h-48 object-contain"
          />
        </div>

        <form onSubmit={handleEmailLogin} className="space-y-4">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              required
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span
              className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-400 cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          <div className="text-right text-sm">
            <Link
              to="/forgot-password"
              className="text-indigo-600 hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            className="w-full py-3 rounded-full font-semibold text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:opacity-90 active:scale-95 transition"
          >
            Log In
          </button>
        </form>

        <button
          onClick={handleGoogleLogin}
          className="w-full py-3 flex items-center justify-center gap-3 rounded-full border border-gray-200 bg-white text-gray-700 font-medium hover:bg-gray-50 transition"
        >
          <img
            src="https://www.svgrepo.com/show/475656/google-color.svg"
            alt="Google"
            className="w-5 h-5"
          />
          Continue with Google
        </button>

        <p className="text-sm text-gray-500 text-center">
          Don’t have an account?{" "}
          <Link
            to="/signup"
            className="text-indigo-600 font-semibold hover:underline"
          >
            Sign up
          </Link>
        </p>

        {message && (
          <p className="text-sm text-red-500 text-center">
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
